/**
 * ReportBuilder — Google Analytics Explore-style report builder with
 * Rows/Columns/Values zones, dimension/metric pickers, and live preview.
 *
 * FR-80: Reports use unfiltered team-level data (no project filter).
 * FR-82: Editable report name.
 * FR-83: Dimensions grouped by category.
 * FR-84: All 14 Meta KPIs + creative metrics.
 * FR-85: Removable chips in zones.
 * FR-86: Dimensions addable to BOTH Rows AND Columns.
 * FR-87: Preview requires >=1 row dimension + >=1 metric.
 * FR-88: Save disabled until >=1 row dimension + >=1 metric.
 * FR-92: Metric labels use (amt) notation.
 * FR-93: Opening saved report pre-populates config.
 */

import {
  DndContext,
  DragOverlay,
  useDroppable,
  useDraggable,
  closestCorners,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import {
  ArrowLeft,
  X,
  Plus,
  ChevronDown,
  ChevronUp,
  Trash2,
  Columns,
} from 'lucide-react';
import { useState, useCallback, useEffect, useMemo } from 'react';

import { ConfirmDeleteModal } from '@/components/ui/ConfirmDeleteModal';
import { useToast } from '@/components/ui/Toast';
import {
  useReportPreview,
  useReportOptions,
  useCreateReport,
  useUpdateReport,
  useDeleteReport,
  useExportReport,
} from '@/hooks/useDashboard';
import { cn } from '@/lib/utils';
import { useDashboardContext } from '@/pages/dashboard/DashboardContext';
import { META_KPI_CATALOG, ReportTooLargeError } from '@/types/dashboard';
import type { SavedReport, ReportConfig } from '@/types/dashboard';

import { ReportPreviewTable } from './ReportPreviewTable';

// ============================================================================
// TYPES
// ============================================================================

export interface ReportBuilderProps {
  /** Existing report to edit, or undefined for new report */
  existingReport?: SavedReport;
  /** Navigate back to report list */
  onBack: () => void;
}

// ============================================================================
// DIMENSION & METRIC CATALOGS
// ============================================================================

/** Dimension item with backend-compatible ID and display label */
export interface DimensionItem {
  id: string;
  label: string;
}

/**
 * Dimension groups — static categories plus dynamic ones from template.
 * For now, static categories cover the required groups (FR-83).
 * IDs match backend extract_dimension_value() expectations:
 *   date_daily, week, month, platform, verdict, creativeName, brandName,
 *   mediaType, placement
 * Template-specific dimensions/taxonomy will be added when template
 * metadata is available in the report builder context.
 */
function getDimensionGroups(): { category: string; items: DimensionItem[] }[] {
  return [
    {
      category: 'Time',
      items: [
        { id: 'date_daily', label: 'Date (Daily)' },
        { id: 'week', label: 'Week' },
        { id: 'month', label: 'Month' },
      ],
    },
    {
      category: 'Platform',
      items: [{ id: 'platform', label: 'Platform' }],
    },
    {
      category: 'Creative Attributes',
      items: [
        { id: 'verdict', label: 'Verdict' },
        { id: 'creativeName', label: 'Creative Name' },
        { id: 'brandName', label: 'Brand' },
        { id: 'mediaType', label: 'Media Type' },
        { id: 'placement', label: 'Placement' },
      ],
    },
  ];
}

/**
 * Metric options with (amt) notation per FR-92.
 * Maps KPI unit to display label suffix.
 */
function getAmtLabel(label: string, unit: string): string {
  if (unit === '$') return `${label} (amt)`;
  if (unit === '%') return `${label} (%)`;
  if (unit === 'x') return `${label} (x)`;
  if (unit === '#') return `${label} (amt)`;
  return label;
}

function getMetricGroups(): { category: string; items: { id: string; label: string }[] }[] {
  const metaKpis = META_KPI_CATALOG.map((kpi) => ({
    id: kpi.id,
    label: getAmtLabel(kpi.label, kpi.unit),
  }));

  const creativeMetrics = [
    { id: 'creative_count', label: 'Creative Count (amt)' },
    { id: 'gate_pass_rate', label: 'Gate Pass Rate (%)' },
  ];

  return [
    { category: 'Meta Performance', items: metaKpis },
    { category: 'Creative', items: creativeMetrics },
  ];
}

// ============================================================================
// DND SUB-COMPONENTS
// ============================================================================

interface DragData {
  type: 'dimension' | 'metric';
  id: string;
  label: string;
}

function DroppableZone({
  id,
  label,
  labelSuffix,
  placeholder,
  children,
}: {
  id: string;
  label: string;
  labelSuffix?: React.ReactNode;
  placeholder: string;
  children: React.ReactNode;
}) {
  const { setNodeRef, isOver } = useDroppable({ id });
  const hasChildren = Array.isArray(children)
    ? children.length > 0
    : !!children;

  return (
    <div
      ref={setNodeRef}
      className={cn(
        'rounded-[10px] border-2 border-dashed p-3 min-h-[64px] transition-colors duration-[180ms]',
        isOver
          ? 'border-brand bg-[rgba(61,90,254,0.05)]'
          : 'border-border-subtle'
      )}
    >
      <div className="text-[10px] text-muted mb-2 font-semibold uppercase tracking-wide">
        {label}
        {labelSuffix}
      </div>
      {hasChildren ? (
        <div className="flex flex-wrap gap-1">{children}</div>
      ) : (
        <div className="text-[10px] text-faint italic">{placeholder}</div>
      )}
    </div>
  );
}

function DraggablePickerItem({
  id,
  label,
  type,
  isSelected,
  onClick,
  secondaryAction,
}: {
  id: string;
  label: string;
  type: 'dimension' | 'metric';
  isSelected: boolean;
  onClick: () => void;
  secondaryAction?: React.ReactNode;
}) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `picker-${type}-${id}`,
    data: { type, id, label } satisfies DragData,
    disabled: isSelected,
  });

  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      className={cn(
        'flex items-center justify-between px-3 py-1.5 text-xs transition-colors',
        isSelected
          ? 'text-muted bg-canvas'
          : 'text-secondary hover:bg-surface-subtle hover:text-primary cursor-grab',
        isDragging && 'opacity-50'
      )}
    >
      <button
        onClick={onClick}
        disabled={isSelected}
        className="flex items-center gap-2 text-left flex-1"
      >
        {isSelected ? (
          <span className="text-success text-[10px]">&#10003;</span>
        ) : (
          <Plus size={10} className="text-muted flex-shrink-0" />
        )}
        {label}
      </button>
      {secondaryAction}
    </div>
  );
}

function DraggableMetricItem({
  id,
  label,
  isSelected,
  onClick,
}: {
  id: string;
  label: string;
  isSelected: boolean;
  onClick: () => void;
}) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `picker-metric-${id}`,
    data: { type: 'metric' as const, id, label } satisfies DragData,
    disabled: isSelected,
  });

  return (
    <button
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      onClick={onClick}
      disabled={isSelected}
      className={cn(
        'w-full flex items-center gap-2 px-3 py-1.5 text-xs text-left transition-colors',
        isSelected
          ? 'text-muted bg-canvas'
          : 'text-secondary hover:bg-surface-subtle hover:text-primary cursor-grab',
        isDragging && 'opacity-50'
      )}
    >
      {isSelected ? (
        <span className="text-success text-[10px]">&#10003;</span>
      ) : (
        <Plus size={10} className="text-muted flex-shrink-0" />
      )}
      {label}
    </button>
  );
}

function DragOverlayChip({
  label,
  type,
}: {
  label: string;
  type: 'dimension' | 'metric';
}) {
  return (
    <div
      className={cn(
        'inline-flex items-center gap-1 px-2.5 py-1 rounded-[10px] text-xs font-semibold shadow-md',
        'bg-surface border border-border-subtle',
        type === 'dimension' ? 'text-brand' : 'text-success'
      )}
    >
      {label}
    </div>
  );
}

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

/** Removable chip inside a zone (FR-85) */
function ZoneChip({
  label,
  onRemove,
  variant = 'dimension',
}: {
  label: string;
  onRemove: () => void;
  variant?: 'dimension' | 'metric';
}) {
  const colors =
    variant === 'metric'
      ? 'bg-success/10 text-success'
      : 'bg-brand/10 text-brand';

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium',
        colors
      )}
    >
      {label}
      <button
        onClick={onRemove}
        className="hover:text-danger transition-colors"
        aria-label={`Remove ${label}`}
      >
        <X size={10} />
      </button>
    </span>
  );
}

/** Collapsible picker panel for dimensions or metrics */
function DimensionPickerPanel({
  groups,
  selectedRows,
  selectedCols,
  onAddToRows,
  onAddToColumns,
}: {
  groups: { category: string; items: DimensionItem[] }[];
  selectedRows: string[];
  selectedCols: string[];
  onAddToRows: (id: string) => void;
  onAddToColumns: (id: string) => void;
}) {
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const allSelected = new Set([...selectedRows, ...selectedCols]);

  const toggle = (cat: string) =>
    setExpanded((prev) => ({ ...prev, [cat]: !prev[cat] }));

  return (
    <div className="rounded-[14px] bg-surface shadow-sm overflow-hidden">
      <div className="px-3 py-2.5 border-b border-border-subtle bg-surface-subtle">
        <div className="text-xs font-semibold text-primary">Dimensions</div>
        <div className="text-[10px] text-muted mt-0.5">
          Drag to Rows or Columns, or click to add.
        </div>
      </div>
      <div className="overflow-y-auto" style={{ maxHeight: 320 }}>
        {groups.map(({ category, items }) => {
          const isOpen = expanded[category] !== false;
          return (
            <div key={category}>
              <button
                onClick={() => toggle(category)}
                className="w-full flex items-center justify-between px-3 py-1.5 bg-canvas border-b border-border-subtle text-[10px] font-semibold text-muted uppercase tracking-wide hover:bg-surface-subtle transition-colors"
              >
                {category}
                {isOpen ? <ChevronUp size={10} /> : <ChevronDown size={10} />}
              </button>
              {isOpen && (
                <div className="py-0.5">
                  {items.map((item) => {
                    const isSelected = allSelected.has(item.id);
                    return (
                      <DraggablePickerItem
                        key={item.id}
                        id={item.id}
                        label={item.label}
                        type="dimension"
                        isSelected={isSelected}
                        onClick={() => !isSelected && onAddToRows(item.id)}
                        secondaryAction={
                          !isSelected ? (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                onAddToColumns(item.id);
                              }}
                              className="p-0.5 text-muted hover:text-brand transition-colors"
                              title={`Add "${item.label}" to Columns`}
                              aria-label={`Add ${item.label} to columns`}
                            >
                              <Columns size={10} />
                            </button>
                          ) : undefined
                        }
                      />
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** Metric picker panel */
function MetricPickerPanel({
  groups,
  selected,
  onAdd,
}: {
  groups: { category: string; items: { id: string; label: string }[] }[];
  selected: string[];
  onAdd: (id: string) => void;
}) {
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const selectedSet = new Set(selected);

  const toggle = (cat: string) =>
    setExpanded((prev) => ({ ...prev, [cat]: !prev[cat] }));

  return (
    <div className="rounded-[14px] bg-surface shadow-sm overflow-hidden">
      <div className="px-3 py-2.5 border-b border-border-subtle bg-surface-subtle">
        <div className="text-xs font-semibold text-primary">Metrics</div>
        <div className="text-[10px] text-muted mt-0.5">
          Drag to Values, or click to add.
        </div>
      </div>
      <div className="overflow-y-auto" style={{ maxHeight: 320 }}>
        {groups.map(({ category, items }) => {
          const isOpen = expanded[category] !== false;
          return (
            <div key={category}>
              <button
                onClick={() => toggle(category)}
                className="w-full flex items-center justify-between px-3 py-1.5 bg-canvas border-b border-border-subtle text-[10px] font-semibold text-muted uppercase tracking-wide hover:bg-surface-subtle transition-colors"
              >
                {category}
                {isOpen ? <ChevronUp size={10} /> : <ChevronDown size={10} />}
              </button>
              {isOpen && (
                <div className="py-0.5">
                  {items.map((item) => {
                    const isSelected = selectedSet.has(item.id);
                    return (
                      <DraggableMetricItem
                        key={item.id}
                        id={item.id}
                        label={item.label}
                        isSelected={isSelected}
                        onClick={() => !isSelected && onAdd(item.id)}
                      />
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ============================================================================
// LABEL LOOKUPS (dimension + metric)
// ============================================================================

/** Build a label map from any dimension groups (static or dynamic). */
function buildDimensionLabelMap(
  groups: { category: string; items: DimensionItem[] }[]
): Map<string, string> {
  const map = new Map<string, string>();
  for (const group of groups) {
    for (const item of group.items) {
      map.set(item.id, item.label);
    }
  }
  return map;
}

/** Static fallback label map (used at module level for initial render). */
const STATIC_DIMENSION_LABEL_MAP = buildDimensionLabelMap(getDimensionGroups());

function buildMetricLabelMap(): Map<string, string> {
  const map = new Map<string, string>();
  for (const group of getMetricGroups()) {
    for (const item of group.items) {
      map.set(item.id, item.label);
    }
  }
  return map;
}

const METRIC_LABEL_MAP = buildMetricLabelMap();

function getMetricLabel(id: string): string {
  return METRIC_LABEL_MAP.get(id) ?? id;
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function ReportBuilder({ existingReport, onBack }: ReportBuilderProps) {
  // Local state for report configuration
  const [reportName, setReportName] = useState(
    existingReport?.name ?? 'Untitled Report'
  );
  const [rowDimensions, setRowDimensions] = useState<string[]>(
    existingReport?.rowDimensions ?? []
  );
  const [columnDimensions, setColumnDimensions] = useState<string[]>(
    existingReport?.columnDimensions ?? []
  );
  const [metrics, setMetrics] = useState<string[]>(
    existingReport?.metrics ?? []
  );
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // FR-93: Reset state when existingReport changes (e.g. opening a different saved report)
  useEffect(() => {
    setReportName(existingReport?.name ?? 'Untitled Report');
    setRowDimensions(existingReport?.rowDimensions ?? []);
    setColumnDimensions(existingReport?.columnDimensions ?? []);
    setMetrics(existingReport?.metrics ?? []);
  }, [existingReport]);

  // Mutations
  const createMutation = useCreateReport();
  const updateMutation = useUpdateReport();
  const deleteMutation = useDeleteReport();
  // Phase 37.1 D-C3: reuse existing export hook for save-then-export orchestrator.
  const { exportReport } = useExportReport();
  const { error: showError } = useToast();

  // Dynamic report options from backend (Phase 36-02)
  const { data: reportOptions } = useReportOptions();
  const { filters } = useDashboardContext();

  // Build config for preview — includes period AND templateId from dashboard filters.
  // Phase 37 D-03: templateId lets the backend resolve template-defined dim/gate
  // names (otherwise LabelContext falls back to camel_to_title). Phase 37 D-04:
  // we do NOT resolve a team-default here — pass through whatever the context
  // has (empty string is a valid documented signal for the camel_to_title fallback).
  const previewConfig = useMemo<ReportConfig>(
    () => ({
      name: reportName,
      templateId: filters.templateId,
      rowDimensions,
      columnDimensions,
      metrics,
      period: filters.period ?? 'all',
      fromDate: filters.period === 'custom' ? filters.fromDate : undefined,
      toDate: filters.period === 'custom' ? filters.toDate : undefined,
    }),
    [
      reportName,
      filters.templateId,
      rowDimensions,
      columnDimensions,
      metrics,
      filters.period,
      filters.fromDate,
      filters.toDate,
    ]
  );

  // Live preview — enabled when >=1 row dimension + >=1 metric (FR-87)
  const hasMinimumConfig = rowDimensions.length >= 1 && metrics.length >= 1;
  const previewQuery = useReportPreview(previewConfig);

  // Dimension groups — use dynamic options from API, fall back to static
  const dimensionGroups = useMemo(() => {
    if (reportOptions?.dimensions) {
      return reportOptions.dimensions;
    }
    // Static fallback while loading
    return getDimensionGroups();
  }, [reportOptions]);
  const metricGroups = useMemo(() => getMetricGroups(), []);

  // Label map — rebuilt when dynamic dimensions load
  const dimensionLabelMap = useMemo(
    () => buildDimensionLabelMap(dimensionGroups),
    [dimensionGroups]
  );

  const getDimensionLabel = useCallback(
    (id: string): string => dimensionLabelMap.get(id) ?? STATIC_DIMENSION_LABEL_MAP.get(id) ?? id,
    [dimensionLabelMap]
  );

  // Zone operations
  const addToRows = useCallback((item: string) => {
    setRowDimensions((prev) => (prev.includes(item) ? prev : [...prev, item]));
  }, []);

  const addToColumns = useCallback((item: string) => {
    setColumnDimensions((prev) =>
      prev.includes(item) ? prev : [...prev, item]
    );
  }, []);

  const addMetric = useCallback((id: string) => {
    setMetrics((prev) => (prev.includes(id) ? prev : [...prev, id]));
  }, []);

  const removeFromRows = useCallback((item: string) => {
    setRowDimensions((prev) => prev.filter((d) => d !== item));
  }, []);

  const removeFromColumns = useCallback((item: string) => {
    setColumnDimensions((prev) => prev.filter((d) => d !== item));
  }, []);

  const removeMetric = useCallback((id: string) => {
    setMetrics((prev) => prev.filter((m) => m !== id));
  }, []);

  // DnD state
  const [activeDrag, setActiveDrag] = useState<DragData | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor)
  );

  const handleDragStart = useCallback((event: DragStartEvent) => {
    const data = event.active.data.current as DragData | undefined;
    if (data) setActiveDrag(data);
  }, []);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      setActiveDrag(null);
      const data = event.active.data.current as DragData | undefined;
      const overId = event.over?.id as string | undefined;
      if (!data || !overId) return;

      // Validate drop: dimensions -> rows/columns only, metrics -> values only
      if (data.type === 'dimension' && overId === 'rows') {
        addToRows(data.id);
      } else if (data.type === 'dimension' && overId === 'columns') {
        addToColumns(data.id);
      } else if (data.type === 'metric' && overId === 'values') {
        addMetric(data.id);
      }
      // All other combinations are no-op (invalid drop)
    },
    [addToRows, addToColumns, addMetric]
  );

  const handleDragCancel = useCallback(() => {
    setActiveDrag(null);
  }, []);

  // Save handler — includes period from dashboard filters so exports use it too.
  // Phase 37 D-03: persist templateId so the saved-report export path resolves
  // template-defined dim/gate labels via backend LabelContext.
  const handleSave = async () => {
    const config: ReportConfig = {
      name: reportName.trim() || 'Untitled Report',
      templateId: filters.templateId, // Phase 37 D-03: persisted so export resolves labels
      rowDimensions,
      columnDimensions,
      metrics,
      period: filters.period ?? 'all',
      fromDate: filters.period === 'custom' ? filters.fromDate : undefined,
      toDate: filters.period === 'custom' ? filters.toDate : undefined,
    };

    try {
      if (existingReport) {
        await updateMutation.mutateAsync({
          reportId: existingReport.reportId,
          config,
        });
      } else {
        await createMutation.mutateAsync(config);
      }
      onBack();
    } catch (err) {
      showError('Failed to save report. Please try again.');
    }
  };

  // Phase 37.1 D-C3: Save-then-export orchestrator.
  // Client-composed from existing mutations — NO new backend endpoint.
  // Invoked by the Preview warning-banner CTA when meta.capApplied === true.
  // Error handling (CONTEXT.md Claude's Discretion):
  //   - Save failure: toast + abort (preserves preview dirty state).
  //   - Export failure after successful save: toast; the saved report remains
  //     in the Reports list so the user can retry from there.
  const handleSaveThenExport = async () => {
    // 1) If name is missing, prompt inline. If user cancels, abort quietly.
    let nameToUse = reportName.trim() || (existingReport?.name ?? '');
    if (!nameToUse || nameToUse === 'Untitled Report') {
      const entered = window.prompt('Name this report to enable full CSV export:');
      if (!entered?.trim()) {
        return;
      }
      nameToUse = entered.trim();
      setReportName(nameToUse);
    }

    const config: ReportConfig = {
      name: nameToUse,
      templateId: filters.templateId, // Phase 37 D-03: MUST carry through for label resolution
      rowDimensions,
      columnDimensions,
      metrics,
      period: filters.period ?? 'all',
      fromDate: filters.period === 'custom' ? filters.fromDate : undefined,
      toDate: filters.period === 'custom' ? filters.toDate : undefined,
    };

    let saved: SavedReport;
    try {
      if (existingReport) {
        saved = await updateMutation.mutateAsync({
          reportId: existingReport.reportId,
          config,
        });
      } else {
        saved = await createMutation.mutateAsync(config);
      }
    } catch (err) {
      showError('Failed to save report. Please try again.');
      return; // Do NOT attempt export if save failed — preserves preview dirty state.
    }

    try {
      await exportReport(saved.reportId, saved.name);
    } catch (err) {
      if (err instanceof ReportTooLargeError) {
        showError(err.message); // Server's verbatim 413 message
      } else {
        showError('Export failed. The report was saved — retry from the Reports list.');
      }
    }
  };

  // Delete handler
  const handleDelete = async () => {
    if (!existingReport) return;
    await deleteMutation.mutateAsync(existingReport.reportId);
    setShowDeleteModal(false);
    onBack();
  };

  const isSaving = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="space-y-3">
      {/* Builder header */}
      <div className="rounded-[14px] bg-surface shadow-sm px-4 py-3 flex items-center gap-3">
        <button
          onClick={onBack}
          className="flex items-center gap-1.5 text-xs text-muted hover:text-brand transition-colors"
        >
          <ArrowLeft size={14} />
          Back to reports
        </button>
        <div className="w-px h-4 bg-surface-subtle" />
        <input
          type="text"
          value={reportName}
          onChange={(e) => setReportName(e.target.value)}
          placeholder="Report name..."
          className="flex-1 text-sm font-semibold text-primary border-none outline-none bg-transparent placeholder:text-muted placeholder:font-normal"
        />
        <div className="flex items-center gap-2">
          {/* Delete button — only for existing reports */}
          {existingReport && (
            <button
              onClick={() => setShowDeleteModal(true)}
              className="flex items-center gap-1 px-2 py-1.5 rounded-[8px] text-xs text-danger hover:bg-danger/10 transition-colors"
            >
              <Trash2 size={12} />
              Delete
            </button>
          )}
          {/* Cancel */}
          <button
            onClick={onBack}
            className="px-3 py-1.5 rounded-[8px] text-xs text-secondary hover:bg-surface-subtle transition-colors"
          >
            Cancel
          </button>
          {/* Save — disabled until minimum config (FR-88) */}
          <button
            onClick={handleSave}
            disabled={!hasMinimumConfig || isSaving}
            className={cn(
              'px-3 py-1.5 rounded-[8px] text-xs font-semibold transition-colors',
              hasMinimumConfig && !isSaving
                ? 'bg-brand text-white hover:bg-[#1A54E8]'
                : 'bg-surface-subtle text-muted cursor-not-allowed'
            )}
          >
            {isSaving ? 'Saving...' : 'Save report'}
          </button>
        </div>
      </div>

      {/* 3-column layout wrapped in DndContext */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCorners}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
        accessibility={{
          announcements: {
            onDragStart({ active }) {
              const data = active.data.current as DragData | undefined;
              return `Picked up ${data?.label ?? 'item'}. Drop into Rows, Columns, or Values zone.`;
            },
            onDragOver({ active, over }) {
              const data = active.data.current as DragData | undefined;
              if (over)
                return `Over ${String(over.id)} zone with ${data?.label ?? 'item'}.`;
              return `${data?.label ?? 'Item'} is not over a drop zone.`;
            },
            onDragEnd({ active, over }) {
              const data = active.data.current as DragData | undefined;
              if (over)
                return `Dropped ${data?.label ?? 'item'} into ${String(over.id)}.`;
              return `Cancelled. ${data?.label ?? 'Item'} returned to picker.`;
            },
            onDragCancel({ active }) {
              const data = active.data.current as DragData | undefined;
              return `Cancelled. ${data?.label ?? 'Item'} returned to picker.`;
            },
          },
        }}
      >
        <div className="grid grid-cols-12 gap-3">
          {/* LEFT: Pickers */}
          <div className="col-span-3 space-y-3">
            <DimensionPickerPanel
              groups={dimensionGroups}
              selectedRows={rowDimensions}
              selectedCols={columnDimensions}
              onAddToRows={addToRows}
              onAddToColumns={addToColumns}
            />
            <MetricPickerPanel
              groups={metricGroups}
              selected={metrics}
              onAdd={addMetric}
            />
          </div>

          {/* CENTER: Zones */}
          <div className="col-span-3">
            <div className="rounded-[14px] bg-surface shadow-sm p-4 space-y-3">
              {/* Rows zone */}
              <DroppableZone
                id="rows"
                label="Rows"
                placeholder="Drop dimensions here"
              >
                {rowDimensions.map((d) => (
                  <ZoneChip
                    key={d}
                    label={getDimensionLabel(d)}
                    onRemove={() => removeFromRows(d)}
                  />
                ))}
              </DroppableZone>

              {/* Columns zone (optional) */}
              <DroppableZone
                id="columns"
                label="Columns"
                labelSuffix={
                  <span className="font-normal normal-case text-faint">
                    {' '}
                    (optional)
                  </span>
                }
                placeholder="Optional breakdown"
              >
                {columnDimensions.map((d) => (
                  <ZoneChip
                    key={d}
                    label={getDimensionLabel(d)}
                    onRemove={() => removeFromColumns(d)}
                  />
                ))}
              </DroppableZone>

              {/* Values zone */}
              <DroppableZone
                id="values"
                label="Values"
                placeholder="Drop metrics here"
              >
                {metrics.map((m) => (
                  <ZoneChip
                    key={m}
                    label={getMetricLabel(m)}
                    onRemove={() => removeMetric(m)}
                    variant="metric"
                  />
                ))}
              </DroppableZone>
            </div>
          </div>

          {/* RIGHT: Preview table */}
          <div className="col-span-6">
            <ReportPreviewTable
              // Phase 37 Plan 05: previewQuery.data is now the full envelope {data, meta}.
              data={previewQuery.data?.data}
              meta={previewQuery.data?.meta}
              isLoading={previewQuery.isLoading}
              hasMinimumConfig={hasMinimumConfig}
              reportName={reportName}
              // Phase 37.1 D-C2/D-C3: banner CTA fires save-then-export orchestrator.
              onSaveThenExport={handleSaveThenExport}
            />
          </div>
        </div>

        <DragOverlay>
          {activeDrag && (
            <DragOverlayChip label={activeDrag.label} type={activeDrag.type} />
          )}
        </DragOverlay>
      </DndContext>

      {/* Delete confirmation modal */}
      {existingReport && (
        <ConfirmDeleteModal
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          onConfirm={handleDelete}
          title="Delete report"
          description="This action cannot be undone. The saved report configuration will be permanently removed."
          itemName={existingReport.name}
          isLoading={deleteMutation.isPending}
        />
      )}
    </div>
  );
}

// Phase 37 D-03 contract assertion: ReportConfig MUST carry templateId so
// preview/save/export bodies ship it. If someone removes the field, this
// type-level assertion will fail to compile at the consumer site above.
// (Intentionally unused runtime value -- purely a compile-time contract.)
const _reportConfigHasTemplateId: (c: ReportConfig) => string | undefined = (c) => c.templateId;
void _reportConfigHasTemplateId;
